import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '../../cn';
import { PageContainer } from './PageContainer';
import { PageHeader } from './PageHeader';

/**
 * PageShell — the one page frame.
 *
 * Every routed page declares the same five slots and lets the frame decide the
 * rest. Before this, a page assembled its own opening: a list page put
 * `PageHeader` straight onto the grey ground, a record page wrapped the same
 * component in a white pinned bar, and each bespoke page picked one of the two
 * — or invented a third. One component, two surfaces, no rule saying which a
 * new page gets.
 *
 * ## The sticking rule
 *
 * `shouldStick` is the whole of it: A PAGE WITH TABS STICKS.
 *
 * That is not a proxy for "the content is long". It is what pinning is FOR. A
 * tab strip that scrolls away turns switching faces into a round trip to the
 * top — the change-history and line-item panels run well past a screen. A title
 * that scrolls away costs nothing: the sidebar still says where you are.
 *
 * There is deliberately NO `sticky` prop. A page cannot opt in or out, so
 * nothing has to police it — the choice is not on offer. If the rule ever needs
 * to change, it changes here, once, for every page at the same time.
 *
 * ## What this shell does NOT do
 *
 * It never renders a `Tabs` root. The `tabs` slot takes finished markup,
 * because the two strips in this app learn about activeness in incompatible
 * ways: `ui/RouteTabs` reads the router, while `ui/Tabs` is a controlled
 * primitive whose triggers must stay inside the SAME root as their panels
 * (#1961/#1969). A page whose panels live below the shell therefore wraps the
 * shell in its own `Tabs` root and hands the strip down — splitting the markup
 * must never split the root.
 */
export interface PageShellProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  /** Already-translated section name. Names the SECTION, not the active tab. */
  title?: string;
  /** Already-translated supporting line under the title. */
  subtitle?: ReactNode;
  /** Inline trim beside the title — a status badge, a count pill. */
  titleAdornment?: ReactNode;
  /** One line above the title — see `PageHeader.breadcrumb`. */
  breadcrumb?: ReactNode;
  /** Extra classes for the title `<h1>` — see `PageHeader.titleClassName`. */
  titleClassName?: string;
  /** Page-level actions, right-aligned. More than a couple: use `PageToolbar`. */
  actions?: ReactNode;
  /**
   * The tab strip, already rendered. Its presence is what pins the title area
   * — see the sticking rule above.
   */
  tabs?: ReactNode;
  children: ReactNode;
  className?: string;
}

/*
 * Standard div attributes forward to the frame, for the same reason
 * `PageContainer` forwards them: a page whose old wrapper carried a
 * `data-testid` must be able to adopt this frame WITHOUT losing it. Dropping
 * them is not a neutral simplification — four Playwright anchors
 * (`procurement-suggestions-page`, `procurement-receive-page`,
 * `stocktake-count-page`, `stocktake-review-page`) vanished that way, and
 * because the e2e suite is manual-run rather than part of PR CI, nothing would
 * have gone red until someone happened to run it.
 */

/**
 * The one predicate. Takes the slots, returns whether the title area pins.
 *
 * Exported so its single caller is not the only thing that can state the rule
 * — a test names the rule directly rather than inferring it from a rendering.
 */
export function shouldStick(slots: { tabs?: ReactNode }): boolean {
  return slots.tabs != null;
}

const MASTHEAD_SURFACE = '-mx-4 -mt-4 border-b border-border bg-card px-4 pb-4 pt-4 space-y-4';

export function PageShell({
  title,
  subtitle,
  titleAdornment,
  breadcrumb,
  titleClassName,
  actions,
  tabs,
  children,
  className,
  ...rest
}: PageShellProps) {
  const sticky = shouldStick({ tabs });
  const header = (
    <PageHeader
      title={title}
      description={subtitle}
      titleAdornment={titleAdornment}
      breadcrumb={breadcrumb}
      titleClassName={titleClassName}
      actions={actions}
    />
  );

  return (
    <PageContainer className={className} {...rest}>
      {/*
        The negative margin is not decoration: it cancels `PageContainer`'s
        `px-4 pt-4` so the bar spans the full content column and its bottom
        rule reads as a real edge rather than a floating line, then restores
        the same padding inside so the title stays aligned with the content
        below it. Those two values MIRROR `PageContainer` — change them
        together (`components/layout/PageContainer.tsx`).

        `z-20` sits above page content and below every overlay: dialogs and
        dropdowns are `z-50`/`z-[60]`, the mobile sidebar scrim is `z-40`.

        `bg-card` is load-bearing, not styling: content scrolling visibly
        under a transparent bar is what a pinned bar must not do.

        Pinned and flowing bars share the same `pb-4`: the segmented strip has no
        baseline padding of its own to give the bar bottom breathing room.
        The shared gap between declared slots is the bar's `space-y-*` rhythm.
      */}
      <div
        data-testid="page-masthead"
        data-sticky={sticky ? 'true' : 'false'}
        className={cn(MASTHEAD_SURFACE, sticky && 'sticky top-0 z-20')}
      >
        {header}
        {tabs}
      </div>
      {children}
    </PageContainer>
  );
}

export default PageShell;
